import React, { useEffect, useMemo, useState } from "react";
import API from "../../../services/api";
import SaveStudioAssetButton from "../SaveStudioAssetButton";
import FieldHint from "../FieldHint";
import useI18n from "../../../i18n/useI18n";
import MotionSurface from "../../../motion/studio/MotionSurface";
import ResultReveal from "../../../motion/studio/ResultReveal";

import ToolResultSkeleton from "../../loaders/ToolResultSkeleton";

import ResultCard from "../../ui/ResultCard";
import ActionRow from "../../ui/ActionRow";
import CopyButton from "../../ui/CopyButton";

import AIActionButton from "../../ui/buttons/AIActionButton";
import UtilityButton from "../../ui/buttons/UtilityButton";

const STUDIO_USAGE_REFRESH_KEY = "hookora_studio_usage_refresh";
const THEME_KEY = "hookora_theme";

const getPreferredLanguage = () => {
  if (typeof window === "undefined") return "en";
  return localStorage.getItem("hookora_language") || "en";
};

const notifyStudioUsageUpdated = () => {
  const value = String(Date.now());
  localStorage.setItem(STUDIO_USAGE_REFRESH_KEY, value);
  window.dispatchEvent(new CustomEvent("hookora:studio-usage-updated"));
};

const angleTypeLabel = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  return raw
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const scoreTone = (score) => {
  const safe = Number(score) || 0;
  if (safe >= 85) {
    return {
      bar: "#16a34a",
      pillBg: "rgba(22,163,74,0.14)",
      pillText: "#16a34a",
    };
  }
  if (safe >= 70) {
    return {
      bar: "#2563eb",
      pillBg: "rgba(37,99,235,0.14)",
      pillText: "#2563eb",
    };
  }
  return {
    bar: "#f59e0b",
    pillBg: "rgba(245,158,11,0.16)",
    pillText: "#b45309",
  };
};

const HookAngleTool = ({
  sharedTopic,
  setSharedTopic,
  sharedPlatform,
  setSharedPlatform,
}) => {
  const { t } = useI18n();

  const getStoredTheme = () => {
    if (typeof window === "undefined") return "light";
    return localStorage.getItem(THEME_KEY) || "light";
  };

  const [audience, setAudience] = useState("");
  const [goal, setGoal] = useState("");
  const [offer, setOffer] = useState("");
  const [painPoint, setPainPoint] = useState("");
  const [desiredOutcome, setDesiredOutcome] = useState("");
  const [brandVoice, setBrandVoice] = useState("");
  const [avoidPhrases, setAvoidPhrases] = useState("");
  const [extraContext, setExtraContext] = useState("");

  const [angles, setAngles] = useState([]);
  const [error, setError] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [theme, setTheme] = useState(getStoredTheme());

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncTheme = () => setTheme(localStorage.getItem(THEME_KEY) || "light");
    window.addEventListener("storage", syncTheme);
    window.addEventListener("hookora-preferences-updated", syncTheme);

    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("hookora-preferences-updated", syncTheme);
    };
  }, []);

  const isDark = theme === "dark";

  const colors = useMemo(
    () => ({
      cardBg: isDark ? "rgba(15,23,42,0.78)" : "#ffffff",
      cardBgStrong: isDark ? "rgba(15,23,42,0.92)" : "#ffffff",
      border: isDark ? "rgba(148,163,184,0.22)" : "#e5e7eb",
      borderStrong: isDark ? "rgba(148,163,184,0.34)" : "#d1d5db",
      text: isDark ? "#f8fafc" : "#111827",
      textMuted: isDark ? "#94a3b8" : "#6b7280",
      textSoft: isDark ? "#cbd5e1" : "#374151",
      inputBg: isDark ? "#0f172a" : "#ffffff",
      inputText: isDark ? "#f8fafc" : "#111827",
      trackBg: isDark ? "#1e293b" : "#e5e7eb",
      panelBg: isDark ? "rgba(30,41,59,0.55)" : "#f8fafc",
      panelBgStrong: isDark ? "rgba(30,41,59,0.78)" : "#f8fafc",
      buttonBg: isDark ? "#e2e8f0" : "#111827",
      buttonText: isDark ? "#020617" : "#ffffff",
      dangerBg: isDark ? "rgba(127,29,29,0.25)" : "#fef2f2",
      dangerText: isDark ? "#fecaca" : "#b91c1c",
      label: isDark ? "#e2e8f0" : "#111827",
      chipBg: isDark ? "rgba(30,41,59,0.9)" : "#f1f5f9",
      chipText: isDark ? "#cbd5e1" : "#475569",
      heroBg: isDark
        ? "linear-gradient(135deg, rgba(30,41,59,0.84), rgba(15,23,42,0.96))"
        : "linear-gradient(135deg, #f8fafc, #ffffff)",
      heroBorder: isDark ? "rgba(129,140,248,0.24)" : "#c7d2fe",
      heroBadgeBg: isDark ? "rgba(99,102,241,0.14)" : "#eef2ff",
      heroBadgeText: isDark ? "#c7d2fe" : "#4338ca",
      heroBadgeBorder: isDark ? "rgba(129,140,248,0.24)" : "#c7d2fe",
      shadow: isDark
        ? "0 16px 34px rgba(2,6,23,0.34)"
        : "0 12px 28px rgba(15,23,42,0.06)",
      shadowSoft: isDark
        ? "0 10px 24px rgba(2,6,23,0.22)"
        : "0 6px 18px rgba(15,23,42,0.05)",
      strategyBg: isDark ? "rgba(37,99,235,0.10)" : "#eff6ff",
      strategyBorder: isDark ? "rgba(96,165,250,0.22)" : "#bfdbfe",
      strategyText: isDark ? "#bfdbfe" : "#1d4ed8",
      sampleBg: isDark ? "rgba(22,163,74,0.10)" : "#ecfdf5",
      sampleBorder: isDark ? "rgba(74,222,128,0.18)" : "#bbf7d0",
      sampleText: isDark ? "#bbf7d0" : "#166534",
    }),
    [isDark]
  );

  const inputStyle = {
    width: "100%",
    padding: "13px 14px",
    borderRadius: "12px",
    border: `1px solid ${colors.borderStrong}`,
    outline: "none",
    boxSizing: "border-box",
    background: colors.inputBg,
    color: colors.inputText,
    fontSize: "14px",
  };

  const textareaStyle = {
    ...inputStyle,
    minHeight: "86px",
    resize: "vertical",
  };

  const labelStyle = {
    display: "flex",
    alignItems: "center",
    marginBottom: "6px",
    color: colors.label,
    fontSize: "14px",
    fontWeight: 600,
  };

  const generateAngles = async () => {
    if (!sharedTopic.trim()) {
      setError(t("hookAngleTool.errors.missingTopic"));
      return;
    }

    try {
      setLoading(true);
      setError("");

      const response = await API.post("/studio/hook-angles", {
        topic: sharedTopic.trim(),
        platform: sharedPlatform,
        audience,
        goal,
        offer,
        pain_point: painPoint,
        desired_outcome: desiredOutcome,
        brand_voice: brandVoice,
        avoid_phrases: avoidPhrases,
        extra_context: extraContext,
        language: getPreferredLanguage(),
      });

      setAngles(Array.isArray(response?.data?.angles) ? response.data.angles : []);
      notifyStudioUsageUpdated();
    } catch (err) {
      console.error("Failed to generate hook angles:", err);
      setError(err?.response?.data?.detail || t("hookAngleTool.errors.generateFailed"));
    } finally {
      setLoading(false);
    }
  };

  const copyAngle = async (angle, id) => {
    const textToCopy = [
      angle?.angle_type ? `${t("hookAngleTool.results.angleType")}: ${angleTypeLabel(angle.angle_type)}` : "",
      angle?.angle_title || "",
      angle?.explanation || "",
      angle?.sample_hook ? `${t("hookAngleTool.results.sampleHook")}: ${angle.sample_hook}` : "",
      angle?.best_use_case ? `${t("hookAngleTool.results.bestUseCase")}: ${angle.best_use_case}` : "",
    ]
      .filter(Boolean)
      .join("\n\n");

    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error("Failed to copy angle:", err);
    }
  };

  const renderScoreBar = (score) => {
    const safe = Number(score) || 0;
    const tone = scoreTone(safe);

    return (
      <div
        style={{
          marginTop: "10px",
          height: "10px",
          borderRadius: "999px",
          background: colors.trackBg,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${Math.max(0, Math.min(100, safe))}%`,
            height: "100%",
            borderRadius: "999px",
            background: tone.bar,
          }}
        />
      </div>
    );
  };

  return (
    <MotionSurface>
    <div style={{ display: "grid", gap: "18px" }}>
      <div
        style={{
          border: `1px solid ${colors.heroBorder}`,
          borderRadius: "20px",
          padding: "18px",
          background: colors.heroBg,
          boxShadow: colors.shadow,
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            padding: "6px 10px",
            borderRadius: "999px",
            background: colors.heroBadgeBg,
            color: colors.heroBadgeText,
            border: `1px solid ${colors.heroBadgeBorder}`,
            fontSize: "12px",
            fontWeight: 800,
            letterSpacing: "0.03em",
            marginBottom: "12px",
          }}
        >
          {t("hookAngleTool.hero.badge")}
        </div>

        <div style={{ color: colors.text, fontSize: "24px", fontWeight: 800, marginBottom: "8px" }}>
          {t("hookAngleTool.hero.title")}
        </div>

        <div style={{ color: colors.textSoft, lineHeight: "1.7", maxWidth: "880px" }}>
          {t("hookAngleTool.hero.description.before")} <strong>{t("hookAngleTool.hero.description.strong")}</strong>{" "}
          {t("hookAngleTool.hero.description.after")}
        </div>
      </div>

      <div
        style={{
          border: `1px solid ${colors.border}`,
          borderRadius: "18px",
          padding: "18px",
          background: colors.cardBg,
          boxShadow: colors.shadowSoft,
        }}
      >
        <div style={{ display: "grid", gap: "14px" }}>
          <div>
            <label style={labelStyle}>
              {t("hookAngleTool.fields.topic.label")}
              <FieldHint text={t("hookAngleTool.fields.topic.hint")} />
            </label>
            <textarea
              value={sharedTopic}
              onChange={(e) => setSharedTopic(e.target.value)}
              placeholder={t("hookAngleTool.fields.topic.placeholder")}
              style={{ ...textareaStyle, minHeight: "92px" }}
            />
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: "12px",
            }}
          >
            <div>
              <label style={labelStyle}>
                {t("hookAngleTool.fields.platform.label")}
                <FieldHint text={t("hookAngleTool.fields.platform.hint")} />
              </label>
              <select
                value={sharedPlatform}
                onChange={(e) => setSharedPlatform(e.target.value)}
                style={inputStyle}
              >
                {[
                  "YouTube",
                  "YouTube Shorts",
                  "Instagram",
                  "Instagram Reels",
                  "TikTok",
                  "X",
                  "LinkedIn",
                  "Facebook",
                ].map((platform) => (
                  <option key={platform} value={platform}>
                    {platform}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={labelStyle}>
                {t("hookAngleTool.fields.audience.label")}
                <FieldHint text={t("hookAngleTool.fields.audience.hint")} />
              </label>
              <input
                value={audience}
                onChange={(e) => setAudience(e.target.value)}
                placeholder={t("hookAngleTool.fields.audience.placeholder")}
                style={inputStyle}
              />
            </div>
          </div>

          <div
            style={{
              padding: "14px",
              borderRadius: "14px",
              background: colors.panelBg,
              border: `1px solid ${colors.border}`,
            }}
          >
            <div style={{ fontWeight: 700, color: colors.text, marginBottom: "12px" }}>
              {t("hookAngleTool.contextTitle")}
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: "12px",
              }}
            >
              <div>
                <label style={labelStyle}>
                  {t("hookAngleTool.fields.goal.label")}
                  <FieldHint text={t("hookAngleTool.fields.goal.hint")} />
                </label>
                <input
                  value={goal}
                  onChange={(e) => setGoal(e.target.value)}
                  placeholder={t("hookAngleTool.fields.goal.placeholder")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("hookAngleTool.fields.offer.label")}
                  <FieldHint text={t("hookAngleTool.fields.offer.hint")} />
                </label>
                <input
                  value={offer}
                  onChange={(e) => setOffer(e.target.value)}
                  placeholder={t("hookAngleTool.fields.offer.placeholder")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("hookAngleTool.fields.painPoint.label")}
                  <FieldHint text={t("hookAngleTool.fields.painPoint.hint")} />
                </label>
                <input
                  value={painPoint}
                  onChange={(e) => setPainPoint(e.target.value)}
                  placeholder={t("hookAngleTool.fields.painPoint.placeholder")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("hookAngleTool.fields.desiredOutcome.label")}
                  <FieldHint text={t("hookAngleTool.fields.desiredOutcome.hint")} />
                </label>
                <input
                  value={desiredOutcome}
                  onChange={(e) => setDesiredOutcome(e.target.value)}
                  placeholder={t("hookAngleTool.fields.desiredOutcome.placeholder")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("hookAngleTool.fields.brandVoice.label")}
                  <FieldHint text={t("hookAngleTool.fields.brandVoice.hint")} />
                </label>
                <input
                  value={brandVoice}
                  onChange={(e) => setBrandVoice(e.target.value)}
                  placeholder={t("hookAngleTool.fields.brandVoice.placeholder")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("hookAngleTool.fields.avoidPhrases.label")}
                  <FieldHint text={t("hookAngleTool.fields.avoidPhrases.hint")} />
                </label>
                <input
                  value={avoidPhrases}
                  onChange={(e) => setAvoidPhrases(e.target.value)}
                  placeholder={t("hookAngleTool.fields.avoidPhrases.placeholder")}
                  style={inputStyle}
                />
              </div>
            </div>

            <div style={{ marginTop: "12px" }}>
              <label style={labelStyle}>
                {t("hookAngleTool.fields.extraContext.label")}
                <FieldHint text={t("hookAngleTool.fields.extraContext.hint")} />
              </label>
              <textarea
                value={extraContext}
                onChange={(e) => setExtraContext(e.target.value)}
                placeholder={t("hookAngleTool.fields.extraContext.placeholder")}
                style={{ ...textareaStyle, minHeight: "96px" }}
              />
            </div>
          </div>

          {error && (
            <div
              style={{
                padding: "12px 14px",
                borderRadius: "12px",
                background: colors.dangerBg,
                color: colors.dangerText,
              }}
            >
              {error}
            </div>
          )}

          <ActionRow>
            <AIActionButton isDark={isDark}
              onClick={generateAngles}
              disabled={loading}
            >
              {loading
                ? t("hookAngleTool.actions.generating")
                : t("hookAngleTool.actions.generate")}
            </AIActionButton>

            <UtilityButton isDark={isDark}
              type="button"
              onClick={() => {
                setAngles([]);
                setError("");
              }}
            >
              Clear Results
            </UtilityButton>
          </ActionRow>
        </div>
      </div>

      {!!angles.length && (
        <div
          style={{
            color: colors.textMuted,
            fontSize: "14px",
            lineHeight: "1.6",
            marginTop: "-4px",
          }}
        >
          {t("hookAngleTool.results.intro.before")} <strong>{t("hookAngleTool.results.intro.strong")}</strong>{" "}
          {t("hookAngleTool.results.intro.after")}
        </div>
      )}

      <div style={{ display: "grid", gap: "14px" }}>
        {loading ? (
          <ToolResultSkeleton isDark={isDark} count={3} />
        ) : (
        angles.map((angle, index) => {
          const safeScore = Number(angle.score) || 0;
          const tone = scoreTone(safeScore);

          return (
            <ResultReveal delay={index * 0.05}>
            <ResultCard
              key={`${angle.angle_title}-${index}`}
              colors={colors}
              index={index}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "12px",
                  flexWrap: "wrap",
                  alignItems: "flex-start",
                  marginBottom: "12px",
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                      flexWrap: "wrap",
                      marginBottom: "10px",
                    }}
                  >
                    {angle.angle_type && (
                      <span
                        style={{
                          background: colors.strategyBg,
                          color: colors.strategyText,
                          border: `1px solid ${colors.strategyBorder}`,
                          borderRadius: "999px",
                          padding: "6px 10px",
                          fontSize: "12px",
                          fontWeight: 800,
                          letterSpacing: "0.02em",
                          textTransform: "uppercase",
                        }}
                      >
                        {angleTypeLabel(angle.angle_type)}
                      </span>
                    )}

                    <span
                      style={{
                        background: tone.pillBg,
                        color: tone.pillText,
                        borderRadius: "999px",
                        padding: "6px 10px",
                        fontSize: "12px",
                        fontWeight: 800,
                      }}
                    >
                      {t("hookAngleTool.results.score")}: {safeScore}
                    </span>
                  </div>

                  <div
                    style={{
                      color: colors.text,
                      fontWeight: 800,
                      fontSize: "20px",
                      lineHeight: "1.45",
                    }}
                  >
                    {angle.angle_title}
                  </div>
                </div>
              </div>

              {angle.explanation && (
                <div
                  style={{
                    color: colors.text,
                    lineHeight: "1.7",
                    whiteSpace: "pre-wrap",
                    marginBottom: "12px",
                  }}
                >
                  {angle.explanation}
                </div>
              )}

              {angle.best_use_case && (
                <div
                  style={{
                    border: `1px solid ${colors.strategyBorder}`,
                    background: colors.panelBgStrong,
                    borderRadius: "14px",
                    padding: "12px",
                    marginBottom: "12px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "12px",
                      fontWeight: 800,
                      color: colors.textMuted,
                      marginBottom: "6px",
                      textTransform: "uppercase",
                      letterSpacing: "0.03em",
                    }}
                  >
                    {t("hookAngleTool.results.bestUseCase")}
                  </div>
                  <div style={{ color: colors.textSoft, lineHeight: "1.65" }}>
                    {angle.best_use_case}
                  </div>
                </div>
              )}

              {angle.sample_hook && (
                <div
                  style={{
                    border: `1px solid ${colors.sampleBorder}`,
                    background: colors.sampleBg,
                    borderRadius: "14px",
                    padding: "12px",
                    marginBottom: "12px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "12px",
                      fontWeight: 800,
                      color: colors.textMuted,
                      marginBottom: "6px",
                      textTransform: "uppercase",
                      letterSpacing: "0.03em",
                    }}
                  >
                    {t("hookAngleTool.results.sampleHook")}
                  </div>
                  <div style={{ color: colors.sampleText, lineHeight: "1.6", fontWeight: 700 }}>
                    {angle.sample_hook}
                  </div>
                </div>
              )}

              {renderScoreBar(safeScore)}

              <ActionRow style={{ marginTop: "14px" }}>
                <CopyButton
                  copied={copiedId === index}
                  onClick={() => copyAngle(angle, index)}
                >
                  {copiedId === index
                    ? t("hookAngleTool.actions.copied")
                    : t("hookAngleTool.actions.copy")}
                </CopyButton>

                <SaveStudioAssetButton
                  toolType="hook_angles"
                  title={`Hook Angles • ${sharedTopic || "Untitled Topic"}`}
                  topic={sharedTopic}
                  platform={sharedPlatform}
                  content={[
                    angle.angle_type ? `${t("hookAngleTool.results.angleType")}: ${angleTypeLabel(angle.angle_type)}` : "",
                    angle.angle_title || "",
                    angle.explanation || "",
                    angle.sample_hook ? `${t("hookAngleTool.results.sampleHook")}: ${angle.sample_hook}` : "",
                    angle.best_use_case ? `${t("hookAngleTool.results.bestUseCase")}: ${angle.best_use_case}` : "",
                  ]
                    .filter(Boolean)
                    .join("\n\n")}
                  meta={{
                    score: angle.score,
                    label: angle.label,
                    angle_type: angle.angle_type || "",
                    sample_hook: angle.sample_hook || "",
                    best_use_case: angle.best_use_case || "",
                  }}
                />
              </ActionRow>
            </ResultCard>
            </ResultReveal>
          );
        })
        )}
      </div>
    </div>
    </MotionSurface>
  );
};

export default HookAngleTool;